import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, User, Lock, ArrowRight, Phone, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { captchaApi, userApi, storage } from '@/user/api';
import type { CaptchaVO } from '@/types/api-entity';

// 导入背景图片和 Logo
import loginBg from '../assest/imgs/loginBg.jpg'; 
import logoImg from '../assest/imgs/kmzs-logo-mini.png'; 

export default function Register() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    captcha: '',
  });
  const [captchaInfo, setCaptchaInfo] = useState<CaptchaVO | null>(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // 加载验证码
  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const result = await captchaApi.getCaptcha();
      if (result.code === 200) {
        setCaptchaInfo(result.data);
      }
    } catch (err) {
      setError('加载验证码失败，请刷新页面重试');
    } finally {
      setCaptchaLoading(false);
    }
  };

  // 页面加载时获取验证码
  useEffect(() => {
    loadCaptcha();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 验证密码
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    if (!captchaInfo) {
      setError('验证码加载失败，请刷新页面');
      return;
    }

    if (!agreeTerms) {
      setError('请阅读并同意服务条款和隐私政策');
      return;
    }

    setLoading(true);

    try {
      const result = await userApi.register({
        username: formData.username,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        nickname: '',
        phone: formData.phone,
        email: formData.email,
        captcha: formData.captcha,
        captchaKey: captchaInfo.captchaKey,
      });

      if (result.code === 200) {
        // 保存用户信息
        storage.setUserInfo(result.data);
        // 跳转到首页
        navigate('/');
      } else {
        setError(result.message || '注册失败');
        // 刷新验证码
        loadCaptcha();
        setFormData({ ...formData, captcha: '' });
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="w-full h-screen flex flex-col relative bg-cover bg-center bg-no-repeat overflow-hidden"
      style={{ backgroundImage: `url(${loginBg})` }}
    >
      {/* 标语区域 - 固定在左侧 */}
      <div className="absolute inset-0 flex flex-col items-start justify-center pl-20 md:pl-32 z-0 pointer-events-none">
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg tracking-wide">
          一站式建材工序生态
        </h1>
        <p className="text-xl md:text-2xl text-white/90 font-medium drop-shadow-md tracking-wider">
          汇聚货源 · 高效集采 · 轻松入驻
        </p>
      </div>
      
      {/* main 区域 (注册框) - 可滚动 */}
      <main className="flex-1 flex items-center justify-end py-12 px-40 relative z-10 overflow-y-auto">
        <div className="w-full max-w-md flex-shrink-0">
          {/* Register Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/20">
            
            {/* Header 横向布局 */}
            <div className="flex items-center justify-center gap-3 mb-8">
              {/* Logo 容器 */}
              <div className="flex items-center">
                <img src={logoImg} alt="Logo" className="w-40 h-24 object-contain" />
              </div>
              
              {/* 标题文本 */}
              <div className="text-left">
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">创建账户</h1>
                <p className="text-sm text-gray-500 mt-1">注册成为互联网生态平台会员</p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm animate-pulse">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-700">用户名 *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    placeholder="请输入用户名（3-20个字符）"
                    value={formData.username}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                    required
                    minLength={3}
                    maxLength={20}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">邮箱地址</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="请输入邮箱地址"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700">手机号</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="请输入手机号"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">密码 *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="请设置密码（6-20个字符）"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                    minLength={6}
                    maxLength={20}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-700">确认密码 *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="请再次输入密码"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 pr-10 h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Captcha */}
              <div className="space-y-2">
                <Label htmlFor="captcha" className="text-gray-700">验证码 *</Label>
                <div className="flex gap-3">
                  <Input
                    id="captcha"
                    name="captcha"
                    type="text"
                    placeholder="请输入验证码"
                    value={formData.captcha}
                    onChange={handleChange}
                    className="h-12 flex-1 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                    required
                    maxLength={4}
                  />
                  <div 
                    className="w-32 h-12 bg-gray-100 rounded-lg overflow-hidden cursor-pointer flex items-center justify-center border border-gray-200 hover:border-blue-400 transition-colors"
                    onClick={loadCaptcha}
                    title="点击刷新验证码"
                  >
                    {captchaLoading ? (
                      <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                    ) : captchaInfo ? (
                      <img 
                        src={captchaInfo.captchaImage} 
                        alt="验证码" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">加载中</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="terms"
                  checked={agreeTerms}
                  onCheckedChange={(checked) => setAgreeTerms(checked as boolean)}
                  className="mt-1"
                />
                <Label htmlFor="terms" className="text-sm text-gray-500 cursor-pointer leading-relaxed">
                  我已阅读并同意{' '}
                  <Link to="/service-agreement" className="text-[#1c5bff] hover:underline">服务条款</Link>
                  {' '}和{' '}
                  <Link to="/privacy-policy" className="text-[#1c5bff] hover:underline">隐私政策</Link>
                </Label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 bg-[#1c5bff] text-white hover:bg-[#1547cc] font-semibold shadow-lg shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-0.5"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> 注册中...
                  </span>
                ) : (
                  <>
                    创建账户 <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Login Link */}
            <p className="text-center mt-8 text-gray-500">
              已有账户？{' '}
              <Link to="/login" className="text-[#1c5bff] font-semibold hover:underline">
                立即登录
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* 固定在屏幕底部的版权信息 */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-20 pointer-events-none">
        <p className="text-white/80 text-sm drop-shadow-md">
          © 2024 孔明平台. All rights reserved.
        </p>
      </div>
    </div>
  );
}